<script setup lang="ts">
/**
 * The minimized call: a dark glass pill under the status bar on phones (bottom-left on desktop) with a
 * beacon dot that pulses with the voice, the timer, what the call is doing, and a red end button. Tap it to
 * go back to the call. On phones it steps aside while the call's own conversation shows its header: the
 * header's green "0:42" chip stands in for it (callChip.ts).
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { callElapsed, formatCallTime, useVoiceCall } from '@/voice/useVoiceCall'
import { useTopIsland } from '@/native/topIsland'

const props = withDefaults(defineProps<{ desktop?: boolean }>(), { desktop: false })
// Phones: first in the top island (src/native/topIsland.ts); the offline pill and banners stack under it.
useTopIsland('call', () => (props.desktop ? 0 : 38))

const call = useVoiceCall()
const st = call.state
const dot = ref<HTMLElement | null>(null)

const label = computed(() => {
    if (st.phase === 'connecting') return 'Calling…'
    if (st.phase === 'onHold') return 'On hold'
    if (st.phase === 'ended') return 'Ended'
    if (st.phase === 'error') return 'Call failed'
    return formatCallTime(callElapsed())
})

const glyph = computed(() => {
    if (st.phase === 'error') return 'error'
    if (st.flags.muted) return 'muted'
    if (st.phase === 'onHold') return 'hold'
    if (st.phase === 'needsApproval') return 'approval'
    if (st.phase === 'speaking') return 'speaking'
    if (st.phase === 'thinking' || st.phase === 'sending') return 'thinking'
    return 'listening'
})

const aria = computed(() => `Call in progress, ${label.value}. Return to call`)

let raf = 0
function tick() {
    const el = dot.value
    if (el) {
        const l = Math.max(call.levels.mic, call.levels.out)
        el.style.setProperty('--lvl', String(Math.min(1, l * 1.4)))
    }
    raf = requestAnimationFrame(tick)
}
onMounted(() => {
    raf = requestAnimationFrame(tick)
})
onBeforeUnmount(() => cancelAnimationFrame(raf))
</script>

<template>
    <div class="vcp" :class="[{ 'is-desk': props.desktop }, `is-${glyph}`]" data-call-overlay>
        <button type="button" class="vcp-main" :aria-label="aria" @click="call.expand()">
            <span ref="dot" class="vcp-dot" aria-hidden="true"><span /></span>
            <span class="vcp-time">{{ label }}</span>
            <span class="vcp-glyph" aria-hidden="true">
                <i v-if="glyph === 'muted'" class="fa-solid fa-microphone-slash" />
                <i v-else-if="glyph === 'hold'" class="fa-solid fa-pause" />
                <i v-else-if="glyph === 'approval'" class="fa-solid fa-hand" />
                <i v-else-if="glyph === 'error'" class="fa-solid fa-triangle-exclamation" />
                <span v-else-if="glyph === 'speaking'" class="vcp-bars"><i /><i /><i /><i /></span>
                <span v-else-if="glyph === 'thinking'" class="vcp-spin" />
                <i v-else class="fa-solid fa-microphone" />
            </span>
        </button>
        <button type="button" class="vcp-end" aria-label="End call" :disabled="st.phase === 'ended'" @click="call.end('user')">
            <i class="fa-solid fa-phone-hangup" />
        </button>
    </div>
</template>

<style scoped>
.vcp {
    position: fixed;
    top: calc(env(safe-area-inset-top, 0px) + 6px);
    left: 50%;
    z-index: var(--z-call, 10090);
    display: flex;
    align-items: center;
    gap: 4px;
    height: 38px;
    padding: 0 4px 0 6px;
    border-radius: 999px;
    color: #fff;
    background: rgb(24 22 26 / 0.86);
    box-shadow: 0 10px 30px -8px rgb(0 0 0 / 0.5), inset 0 0 0 0.5px rgb(255 255 255 / 0.14), inset 0 1px 0 rgb(255 255 255 / 0.08);
    -webkit-backdrop-filter: blur(24px) saturate(160%);
    backdrop-filter: blur(24px) saturate(160%);
    transform: translateX(-50%);
}
.vcp.is-desk {
    top: auto;
    /* Clear of the dashboard's left nav rail. */
    left: var(--vc-pill-left, 108px);
    bottom: 24px;
    height: 44px;
    padding: 0 5px 0 8px;
    transform: none;
}
.vcp-main {
    display: flex;
    align-items: center;
    gap: 9px;
    height: 100%;
    padding: 0 8px 0 4px;
    border: 0;
    background: none;
    color: inherit;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}
.vcp-dot {
    --lvl: 0;
    position: relative;
    width: 12px;
    height: 12px;
}
.vcp-dot::before,
.vcp-dot span {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
}
.vcp-dot::before {
    background: rgb(251 76 31 / 0.45);
    transform: scale(calc(1 + var(--lvl) * 1.3));
    transition: transform 90ms linear;
}
.vcp-dot span {
    background: radial-gradient(circle at 35% 35%, #fff 0, #ffd2c2 30%, #fb4c1f 75%);
    box-shadow: 0 0 10px rgb(251 76 31 / 0.8);
}
.vcp.is-muted .vcp-dot span, .vcp.is-hold .vcp-dot span { background: #8e8e93; box-shadow: none; }
.vcp.is-approval .vcp-dot span { background: #ffb020; box-shadow: 0 0 10px rgb(255 176 32 / 0.8); }
.vcp.is-error .vcp-dot span { background: #ff453a; box-shadow: none; }
.vcp-time {
    min-width: 34px;
    font-size: 14px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
}
.vcp-glyph {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    font-size: 12px;
    color: rgb(255 255 255 / 0.75);
}
.vcp.is-approval .vcp-glyph { color: #ffb020; }
.vcp-bars { display: flex; align-items: center; gap: 2px; height: 12px; }
.vcp-bars i {
    width: 2.5px;
    height: 12px;
    border-radius: 2px;
    background: #fff;
    transform-origin: center;
    animation: vcp-bar 0.9s ease-in-out infinite;
}
.vcp-bars i:nth-child(2) { animation-delay: 0.15s; }
.vcp-bars i:nth-child(3) { animation-delay: 0.3s; }
.vcp-bars i:nth-child(4) { animation-delay: 0.45s; }
@keyframes vcp-bar { 0%, 100% { transform: scaleY(0.35); } 50% { transform: scaleY(1); } }
.vcp-spin {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid rgb(255 255 255 / 0.2);
    border-top-color: #fff;
    animation: vcp-spin 0.9s linear infinite;
}
@keyframes vcp-spin { to { transform: rotate(360deg); } }
.vcp-end {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 999px;
    color: #fff;
    font-size: 12px;
    background: #ff3b30;
    cursor: pointer;
    transition: transform 200ms cubic-bezier(0.34, 1.4, 0.64, 1);
}
.vcp.is-desk .vcp-end { width: 34px; height: 34px; font-size: 13px; }
.vcp-end:active { transform: scale(0.88); }
.vcp-end:disabled { opacity: 0.5; }
@media (prefers-reduced-motion: reduce) {
    .vcp-bars i, .vcp-spin { animation: none; }
    .vcp-dot::before { transition: none; }
}
</style>
